import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { UpdatePipelineStageMappingBody } from '../api/pipelineStageMappings';
import {
  getPipelineStageMappings,
  updatePipelineStageMapping,
} from '../api/pipelineStageMappings';
import type { PipelineStageMappingsResponse } from '../types';

export function usePipelineStageMappings(orgId: number | string | undefined) {
  const queryClient = useQueryClient();

  const mappingsQuery = useQuery<PipelineStageMappingsResponse>({
    queryKey: ['hubspotPipelineStageMappings', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getPipelineStageMappings(orgId);
    },
    enabled: !!orgId,
  });

  const mutateMapping = useCallback(
    (body: UpdatePipelineStageMappingBody) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return updatePipelineStageMapping(orgId, body);
    },
    [orgId]
  );

  const updateMapping = useMutation({
    mutationFn: mutateMapping,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['hubspotPipelineStageMappings', orgId],
      }),
  });

  return {
    mappings: mappingsQuery,
    updateMapping,
    isLoading: mappingsQuery.isLoading,
    error: mappingsQuery.error,
  };
}
